/**
 * Publishes the current map viewport (2D bounds / 3D camera) into the viewport
 * store on each debounced camera settle, then re-renders viewport-culled layers
 * so dense feeds (CCTV, aircraft, vessels) draw only what's in view. Mirrors the
 * dual-mode, map-swap-aware wiring of use-h3-subscriptions. Sparse/global layers
 * (satellites) are unaffected.
 */

import { useEffect } from 'react';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import { useMapStore } from '@/stores/map-store';
import { useMapViewportStore } from '@/stores/map-viewport-store';
import { viewportFrom2D } from '@/map/markers/viewport-2d';
import { viewportFrom3D } from '@/map/markers/viewport-3d';
import { reRenderVisibleTrackLayers } from './use-track-layers';

/** Trailing debounce after a camera settle before re-culling (ms). */
const SETTLE_DEBOUNCE_MS = 120;

/** Wire camera-settle → viewport store → re-cull of culled layers (2D + 3D). */
export function useViewportCulling(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  const viewMode = useMapStore((s) => s.viewMode);

  useEffect(() => {
    let cancelled = false;
    let detach: (() => void) | null = null;
    let pending: ReturnType<typeof setTimeout> | null = null;

    const recompute = (): void => {
      const viewport = readViewport(mapRef.current, modeRef.current);
      if (!viewport) return;
      useMapViewportStore.getState().setViewport(viewport);
      reRenderVisibleTrackLayers(mapRef, modeRef);
    };

    const onSettle = (): void => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(recompute, SETTLE_DEBOUNCE_MS);
    };

    const attach = (): void => {
      const map = mapRef.current;
      if (!map) return;
      if (modeRef.current === '2d') {
        const listener = (map as google.maps.Map).addListener('idle', onSettle);
        detach = () => listener.remove();
      } else {
        // range/center change events fire on every camera move — wheel-zoom, drag,
        // AND programmatic flyCameraTo (gmp-steadychange misses the latter) — so we
        // debounce them rather than relying on the steady event.
        const map3d = map as google.maps.maps3d.Map3DElement;
        map3d.addEventListener('gmp-rangechange', onSettle);
        map3d.addEventListener('gmp-centerchange', onSettle);
        detach = () => {
          map3d.removeEventListener('gmp-rangechange', onSettle);
          map3d.removeEventListener('gmp-centerchange', onSettle);
        };
      }
      recompute();
    };

    const waitForMapReady = (): void => {
      if (cancelled) return;
      if (mapRef.current && modeRef.current === viewMode) {
        attach();
        return;
      }
      requestAnimationFrame(waitForMapReady);
    };
    waitForMapReady();

    return () => {
      cancelled = true;
      if (pending) clearTimeout(pending);
      if (detach) detach();
    };
  }, [viewMode]);
}

/** Read the current viewport for the active mode, or null if the map isn't ready. */
function readViewport(map: MapInstance | null, mode: ViewMode): ReturnType<typeof viewportFrom2D> {
  if (!map) return null;
  return mode === '2d'
    ? viewportFrom2D(map as google.maps.Map)
    : viewportFrom3D(map as google.maps.maps3d.Map3DElement);
}
